import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { sideBarItem } from '@/components/Sidebar/constants';
import { useRootSelector } from '@/hooks/selector.hook';
import { flattenSidebarItems } from '@/utils/sidebar';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const INTERNAL_ALLOWED_ROUTES = [
  '/admin/notifications',
  '/account-claimed',
  '/admin/profile',
  '/admin/settings',
];

const normalizeRouteForMatch = (route?: string | null) => {
  if (!route) return '';

  const safeRoute = route.startsWith('/') ? route : `/${route}`;
  return safeRoute === '/' ? safeRoute : safeRoute.replace(/\/+$/, '');
};

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated } = useRootSelector((state) => state.auth);
  const { dataSidebar } = useRootSelector((state) => state.sidebar);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRoutes = useMemo(() => {
    const apiRoutes = flattenSidebarItems(dataSidebar)
      .map((item) => (item.key === 'admin-form-fields' ? '/admin/form-fields' : item.route))
      .filter(Boolean);

    if (apiRoutes.length) {
      return apiRoutes;
    }

    return sideBarItem.flatMap((section) => section.items.map((item) => item.route));
  }, [dataSidebar]);

  useEffect(() => {
    const matchesRoute = (route?: string | null) => {
      const normalizedRoute = normalizeRouteForMatch(route);
      const normalizedPath = normalizeRouteForMatch(location.pathname);

      if (!normalizedRoute) return false;

      return normalizedPath === normalizedRoute || normalizedPath.startsWith(normalizedRoute + '/');
    };

    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    const isAllowed =
      INTERNAL_ALLOWED_ROUTES.some((route) => matchesRoute(route)) ||
      sidebarRoutes.some((route) => matchesRoute(route));

    if (!isAllowed) {
      const firstRoute = sidebarRoutes[0];
      if (firstRoute) {
        navigate(firstRoute, { replace: true });
      }
    }
  }, [isAuthenticated, location.pathname, navigate, sidebarRoutes]);

  return <>{children}</>;
}
